import React from 'react';
import {createRoot} from 'react-dom/client';
import TranslationsInitializer from 'js/tool/translationsInitializer';
import AuthProvider from 'js/reusable/auth/provider';
import MetaDataInitializer from 'js/tool/metaData/provider';
import 'scss/fbs.scss';
import {BrowserRouter} from 'react-router-dom';
import EContractSignatureWrapper from 'js/reusable/eContract/signWrapper';
import UpdateLayer from 'js/tool/updateLayer';
import 'animate.css';
import ErrorBoundaryComp from 'js/reusable/errorBoundary';
import dayjs from 'dayjs';
//@ts-ignore: no types
import dayjsBusinessDays from 'dayjs-business-days';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isoWeek from 'dayjs/plugin/isoWeek';
import timezone from 'dayjs/plugin/timezone';
import createCache from '@emotion/cache';
import {CacheProvider} from '@emotion/react';

import 'js/tool/sentry';

window.global ||= window;

function importApplicationContainer() {
	if (import.meta.env.REACT_APP_BUILD_TARGET === 'app') {
		return import('js/application/container');
	}

	return import('js/admin/components/container');
}

dayjs.extend(customParseFormat);
dayjs.extend(dayjsBusinessDays);
dayjs.extend(utc);
dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);
dayjs.extend(timezone);

const container = document.getElementById('root');
const root = createRoot(container!);

// This ensures that Emotion's styles are inserted before Tailwind's styles
// so that Tailwind classes have precedence over Emotion
// See for bigger example:
// https://github.com/JedWatson/react-select/blob/master/storybook/stories/ClassNamesWithTailwind.stories.tsx

// Fix the missing prop validation when converting to typescript
// eslint-disable-next-line react/prop-types
function EmotionCacheProvider({children}: {children: React.ReactNode}) {
	const cache = React.useMemo(
		() =>
			createCache({
				key:            'with-tailwind',
				insertionPoint: document.querySelector('title')!,
			}),
		[]
	);

	return <CacheProvider value={cache}>{children}</CacheProvider>;
}

importApplicationContainer().then(({default: AppContainer}) =>
	root.render(
		<React.StrictMode>
			<EmotionCacheProvider>
				<ErrorBoundaryComp>
					<UpdateLayer>
						<TranslationsInitializer>
							<BrowserRouter
								basename="/"
							>
								<EContractSignatureWrapper>
									<AuthProvider>
										<MetaDataInitializer>
											<AppContainer />
										</MetaDataInitializer>
									</AuthProvider>
								</EContractSignatureWrapper>
							</BrowserRouter>
						</TranslationsInitializer>
					</UpdateLayer>
				</ErrorBoundaryComp>
			</EmotionCacheProvider>
		</React.StrictMode>
	)
);
